/**
 * Keep the keyboard inside a modal surface (redesign §19–20, §23 under 1024,
 * rule 31): Tab from the last control comes back to the first and Shift+Tab
 * from the first goes to the last; the focus goes in when it opens if nothing
 * inside has it yet, and back to what had it before when it closes.
 *
 * `active` lets a surface trap only when it is modal — the side panel is
 * modal under 1024 and not above.
 */
import { useEffect, useRef } from 'react';
import type React from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The controls Tab reaches inside `root`, in order, visible ones only. */
export function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((one) => one.getClientRects().length > 0);
}

export function useFocusTrap(root: React.RefObject<HTMLElement | null>, active = true): void {
  // What had the focus when the surface first rendered — read during render,
  // before an effect of the surface's own (a dialog's field) moves it inside.
  const before = useRef<HTMLElement | null | undefined>(undefined);
  if (before.current === undefined) before.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  useEffect(() => {
    if (!active) return;
    const opener = before.current;
    const box = root.current;
    if (box && !box.contains(document.activeElement)) (focusables(box)[0] ?? box).focus();
    const onKey = (event: KeyboardEvent): void => {
      const here = root.current;
      if (event.key !== 'Tab' || !here) return;
      const all = focusables(here);
      if (all.length === 0) {
        event.preventDefault();
        return;
      }
      const first = all[0];
      const last = all[all.length - 1];
      const inside = here.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Back to the button that opened it, if it is still on the page.
      if (opener?.isConnected) opener.focus();
    };
  }, [root, active]);
}
